import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';

export const downloadPredictionDocx = async ({ code, title, summary, details, language = 'ru', patientName, results }) => {
  // Форматирование текста с переносами строк
  const formatParagraphs = (text) => {
    if (!text) return [];
    return text.split('\n\n').filter(Boolean).map(section => {
      const lines = section.split('\n').filter(Boolean);
      return new Paragraph({
        children: lines.map((line, idx) => [
          new TextRun({ text: line, break: idx > 0 ? 1 : 0 })
        ]).flat(),
        spacing: { after: 200 },
      });
    });
  };

  // Хелпер для создания строки с результатом
  const createResultParagraph = (label, value) => new Paragraph({
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 22, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
      new TextRun({ text: value, size: 22, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
    ],
    spacing: { after: 100 },
  });

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: 'normal',
          name: 'Normal',
          run: { font: language === 'ru' ? 'Times New Roman' : 'Calibri', size: 24, color: '2c3e50' },
          paragraph: { spacing: { after: 200, line: 360, lineRule: 'auto' } },
        },
      ],
    },
    sections: [{
      properties: {},
      children: [
        // Заголовок
        new Paragraph({
          text: title || `Prediction: ${code}`,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
          run: { font: language === 'ru' ? 'Times New Roman' : 'Calibri', bold: true, size: 32, color: '1565c0' },
        }),
        
        // Код прогноза (бейдж)
        new Paragraph({
          children: [
            new TextRun({
              text: ` ${code} `,
              bold: true,
              color: 'ffffff',
              shading: { fill: '2196f3', color: 'ffffff' },
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        }),

        // ФИО пациента (если передано и включен чекбокс)
        ...(patientName ? [
          new Paragraph({
            children: [
              new TextRun({ text: language === 'ru' ? 'ФИО пациента: ' : 'Patient Name: ', bold: true, size: 24, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
              new TextRun({ text: patientName, size: 24, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
            ],
            spacing: { after: 200 },
          })
        ] : []),

        // Мета-информация
        new Paragraph({
          children: [
            new TextRun({ text: `${language === 'ru' ? 'Дата' : 'Date'}: ${new Date().toLocaleDateString(language)}`, italics: true, color: '666666', size: 20, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
            new TextRun({ text: ' | ', color: '666666', size: 20, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
            new TextRun({ text: `${language === 'ru' ? 'Язык' : 'Language'}: ${language === 'ru' ? 'Русский' : language === 'pt' ? 'Português' : 'English'}`, italics: true, color: '666666', size: 20, font: language === 'ru' ? 'Times New Roman' : 'Calibri' }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),

        // Разделитель
        new Paragraph({
          border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'dee2e6' } },
          spacing: { after: 300 },
        }),

        // Результаты анализа (углы и индексы)
        ...(results ? [
          new Paragraph({
            text: language === 'ru' ? '📊 Результаты анализа' : '📊 Analysis Results',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 150 },
            run: { bold: true, size: 26, color: '2c3e50', font: language === 'ru' ? 'Times New Roman' : 'Calibri' },
          }),
          createResultParagraph('SNA', `${results.sna?.toFixed(1)}°`),
          createResultParagraph('SNB', `${results.snb?.toFixed(1)}°`),
          createResultParagraph('ANB', `${results.anb?.toFixed(1)}°`),
          createResultParagraph('ML/SNL', results.mlSnlReal !== null && results.mlSnlReal !== undefined ? `${results.mlSnlReal.toFixed(1)}°` : '--'),
          createResultParagraph('NL/NSL', results.nlNslReal !== null && results.nlNslReal !== undefined ? `${results.nlNslReal.toFixed(1)}°` : '--'),
          createResultParagraph('T1', results.t1 !== null && results.t1 !== undefined ? `${results.t1.toFixed(1)}°` : '--'),
          createResultParagraph('T2', results.t2 !== null && results.t2 !== undefined ? `${results.t2.toFixed(1)}°` : '--'),
          createResultParagraph('T3', results.t3 !== null && results.t3 !== undefined ? `${results.t3.toFixed(1)}°` : '--'),
        ] : []),

        // Краткое описание (Summary)
        new Paragraph({
          text: language === 'ru' ? '📋 Краткое описание' : '📋 Summary',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 150 },
          run: { bold: true, size: 26, color: '2c3e50', font: language === 'ru' ? 'Times New Roman' : 'Calibri' },
        }),
        ...formatParagraphs(summary),

        // Детальное описание (Details)
        new Paragraph({
          text: language === 'ru' ? '📖 Подробное описание' : '📖 Detailed Description',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 150 },
          run: { bold: true, size: 26, color: '2c3e50', font: language === 'ru' ? 'Times New Roman' : 'Calibri' },
        }),
        ...formatParagraphs(details),

        // Футер
        new Paragraph({
          text: '---',
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 100 },
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: language === 'ru' 
                ? 'Документ сгенерирован автоматически • Анализ по Петровичу' 
                : 'Document generated automatically • Petrovich Cephalometric Analysis',
              italics: true,
              color: '999999',
              size: 18,
              font: language === 'ru' ? 'Times New Roman' : 'Calibri',
            }),
          ],
          alignment: AlignmentType.CENTER,
        }),
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `prediction_${code}_${new Date().toISOString().slice(0,10)}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};